/**
 * Harvest — shared 3-layer design tokens for Kapture AI.
 *
 * Layer 1: Primitives  — raw colour/type/radius/motion values.
 * Layer 2: Semantics   — intent aliases (surface.workspace, status.synced…).
 * Layer 3: Component   — per-component contracts (evidenceItem, sourceMarker…).
 *
 * Mirrors `tokens.css` exactly; read the header there for the reasoning behind
 * the cream ground, the two-strength flame, and the warm dark surfaces.
 *
 * The flat `T` export keeps every pre-Harvest key alive so existing
 * inline-styled screens (mobile) and tests keep working while consumers
 * migrate to the layered objects below. Keys that name an intent (good/warn/
 * danger/surface/hud…) now resolve to Harvest values; keys that name an old
 * raw primitive (fieldGreen, obsidianBg…) keep their original value and are
 * deprecated.
 */

/* ------------------------------------------------------------ primitives -- */

export const palette = {
  /**
   * Harvest Flame — action, active capture, selection, focus. Never decor,
   * and never a letterform: `signal` is the fill, `signalText` is the word.
   * #FA5D00 measures 3.17:1 on white and 3.01:1 on cream, which carries a
   * dot, a bar or a 24px headline and fails every word under that.
   */
  signal: '#FA5D00',
  /**
   * Hover/pressed fill — the darkest "darken ~10%" that still holds ink at
   * AA (4.62). One step further and every primary button's hover state falls
   * under the bar.
   */
  signalHover: '#EB5400',
  /** Deep accent + focus ring: 3.96 on cream, which the hover cut is not. */
  signalDeep: '#D84F00',
  signalSoft: '#FFEEE4',
  /**
   * Text-grade cuts of the accent/status hues. These pass ≥4.5:1 on white,
   * cream, the sunken well, and their own soft chips — use them whenever the
   * hue is carrying words. `signalText` sits 0.2° from `signal` in OKLCH hue,
   * so the pair reads as one colour at two strengths.
   */
  signalText: '#C04600',
  sageText: '#1A6B46',
  amberText: '#8A5E00',
  alertText: '#A3231B',
  infoText: '#2B5580',

  /** Atmospheric only — decorative warmth behind product UI, never functional. */
  marigold: '#FEE3B5',
  /** The shadow hue, and warm hairlines on cream. */
  parchment: '#E3D6C5',

  /**
   * Warm dark. Kept dark because it is functional — the capture HUD is read in
   * direct sun and frames a viewfinder — but retuned onto the ink hue so it
   * belongs to the same system as the cream. Flame is 5.46:1 on `graphite`,
   * so on dark the fill value is also the text value.
   */
  graphite: '#1A1B18',
  /** Coal — nav spine, technical context, raised dark surfaces. */
  coal: '#232420',
  coal2: '#2E2F2A',

  /** Ground. Cream is the canvas; white floats above it as cards. */
  white: '#FFFFFF',
  cream: '#FFF8F1',
  cream2: '#FAF1E7',
  cream3: '#F2E7DA',
  /** Pre-Harvest names for the light ground, kept pointing at the new planes. */
  mineral: '#FFF8F1',
  mineral2: '#FAF1E7',
  mineral3: '#F2E7DA',

  /** Ink — warm near-black ladder. Ratios stated against cream / white. */
  ink: '#1D1E1C', // 15.90 / 16.74
  ink2: '#4A4A47', // 8.45 /  8.89 — ironwood
  ink3: '#615F5C', // 6.05 /  6.36 — warm stone
  inkDim: '#615F5C',
  inkMute: '#6E6C68', // 4.98 /  5.24 — ash, darkened to clear AA on cream
  /** Placeholders, input boundaries, decorative strokes. Never small copy. */
  driftwood: '#8E8B87', // 3.22 / 3.39
  bone: '#C0BBB6',
  smoke: '#A5A19C',
  mist: '#D9D9D9',

  /** Statuses. Bright cut for fills/dots/borders, `*Text` cut for words. */
  sage: '#2F8F63',
  sageSoft: '#E6F2EA',
  amber: '#A97A08',
  amberSoft: '#FBF0D8',
  alert: '#BB2F26',
  alertSoft: '#FBEAE8',
  info: '#3B6EA5',
  infoSoft: '#E8EFF7',

  /** Text on warm-dark surfaces. Mute stays ≥4.5:1 on coal. */
  onDark: '#FDF6EE',
  onDarkDim: 'rgba(253,246,238,0.66)',
  onDarkMute: 'rgba(253,246,238,0.52)',
  onDarkLine: 'rgba(253,246,238,0.14)',
} as const;

export const typography = {
  /** Headings, body, actions, navigation. MuotoWeb, with Inter as the
   *  documented substitute and the face actually loaded. */
  ui: '"MuotoWeb", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  /** Hero display serif. One 72px headline per page — never body, never
   *  under 48px, or the contrast that makes it work is spent. */
  display: '"Monarch", "Newsreader", "Iowan Old Style", Georgia, serif',
  /** Record IDs, timestamps, coordinates, measurements, sync state. */
  metadata: '"DM Mono", "JetBrains Mono", ui-monospace, monospace',
  /** Positive tracking is part of the face's warmth, not a flourish. */
  tracking: '0.015em',
} as const;

export const radius = {
  /** Chips, source markers, controls too small to carry 16 gracefully. */
  control: 12,
  /** Buttons, inputs, evidence items, images. */
  item: 16,
  /** Cards and grouped regions. */
  panel: 20,
  /** Primary workspace surfaces. */
  surface: 20,
  /** Tags and status pills. */
  pill: 999,
} as const;

export const motion = {
  ease: 'cubic-bezier(0.16, 1, 0.3, 1)',
  fast: '120ms',
  base: '180ms',
  slow: '220ms',
} as const;

/** Warm-tinted, always. A neutral-grey shadow over warm ink reads as grime. */
export const elevation = {
  sm: 'rgba(0,0,0,0.2) 0px 1px 4px 0px',
  md: 'rgba(227,214,197,0.6) 0px 4px 16px 0px',
  lg: 'rgba(250,166,0,0.25) 6px 4px 24px 0px',
} as const;

/* ------------------------------------------------------------- semantics -- */

export const semantic = {
  surface: {
    /** Page canvas — the cream ground the whole product reads on. */
    workspace: palette.cream,
    /** Dark operations ground — capture HUD, viewfinder chrome. */
    hud: palette.graphite,
    /**
     * Raised dark rail — the navigation spine only. Despite the name this
     * is NOT the evidence/decision rail, which reads on paper: repointing
     * it here would relight WebShell and the mobile AppShell instead.
     */
    rail: palette.coal,
    /** Second dark elevation — chips and wells on the rail. */
    railRaised: palette.coal2,
    /** Light evidence reading canvas. */
    evidence: palette.cream,
    /** Raised card floating on the cream canvas. */
    evidenceRaised: palette.white,
    /** Recessed well on the cream canvas. */
    evidenceSunken: palette.cream2,
  },
  text: {
    primary: palette.ink,
    secondary: palette.ink3,
    faint: palette.inkMute,
    /** Metadata is set in the mono stack at reduced size. */
    metadata: palette.ink3,
    /** Brand-coloured link/label text on light surfaces (AA at small sizes). */
    link: palette.signalText,
    onDark: palette.onDark,
    onDarkSecondary: palette.onDarkDim,
    onDarkFaint: palette.onDarkMute,
  },
  border: {
    /**
     * The one border that carries a component boundary, held to WCAG 1.4.11's
     * 3:1. Harvest names #C0BBB6 for input outlines; at 1.90:1 on white it
     * cannot be the only thing telling a user where a field is, so `soft`
     * keeps that value for outlines nothing depends on seeing.
     */
    field: palette.driftwood,
    soft: palette.bone,
    divider: palette.mist,
    dividerWarm: palette.parchment,
  },
  status: {
    synced: palette.sage,
    verified: palette.sage,
    offline: palette.amber,
    attention: palette.amber,
    blocked: palette.alert,
    rejected: palette.alert,
  },
  action: {
    primary: palette.signal,
    primaryHover: palette.signalHover,
    /** Filled-CTA foreground: ink on flame is 5.28:1, white on flame 3.17:1. */
    primaryInk: palette.ink,
  },
  record: {
    /** Selected/active record or evidence entry. */
    active: palette.signal,
  },
  focusRing: palette.signal,
} as const;

/* -------------------------------------------------------------- component -- */

/** Evidence source families. Colour is never used alone — always icon + label. */
export type EvidenceSource = 'photo' | 'video' | 'voice' | 'form' | 'note' | 'location' | 'ai';

export const sourceMarker: Record<EvidenceSource, { base: string; soft: string; label: string }> = {
  photo: { base: '#3F6FB5', soft: '#EEF3FA', label: 'Photo' },
  video: { base: '#3F6FB5', soft: '#EEF3FA', label: 'Video' },
  voice: { base: '#8B57AD', soft: '#F6F1F9', label: 'Voice' },
  form: { base: '#A85179', soft: '#FAF1F5', label: 'Form' },
  note: { base: '#6F6B66', soft: '#EFEEEC', label: 'Note' },
  location: { base: '#256F79', soft: '#E5F0F1', label: 'Location' },
  ai: { base: '#6A5CC0', soft: '#F1EEFA', label: 'AI assist' },
};

export const component = {
  evidenceItem: {
    radius: radius.item,
    selectedBorder: semantic.record.active,
    connector: palette.parchment,
    connectorOnDark: palette.onDarkLine,
  },
  /** The evidence/decision rail — paper, like the record beside it. */
  recordRail: {
    bg: semantic.surface.evidenceRaised,
    raised: semantic.surface.evidenceSunken,
    line: palette.parchment,
  },
  captureAperture: {
    ring: palette.signal,
    idle: palette.onDark,
  },
  statusBadge: {
    synced: { fg: palette.sageText, bg: palette.sageSoft, border: 'rgba(47,143,99,0.38)' },
    offline: { fg: palette.amberText, bg: palette.amberSoft, border: 'rgba(169,122,8,0.38)' },
    blocked: { fg: palette.alertText, bg: palette.alertSoft, border: 'rgba(187,47,38,0.38)' },
    active: { fg: palette.signalText, bg: palette.signalSoft, border: 'rgba(250,93,0,0.38)' },
  },
  reviewDecision: {
    approve: palette.sage,
    reject: palette.alert,
  },
  focusRing: {
    width: 2,
    offset: 2,
    color: semantic.focusRing,
  },
} as const;

/* ---------------------------------------------------- flat legacy surface -- */

export const T = {
  // Layer 1 primitives kept under their original names. The first block are
  // deprecated raw values retained verbatim for compatibility; do not use in
  // new work — reach for `palette`/`semantic` instead.
  brandPrimary: '#FA5D00',
  brandDark: '#EB5400',
  brandSoft: '#FFEEE4',
  slateDark: '#12171E',
  obsidianBg: '#0B0F19',
  surfaceElevated: '#1E293B',
  fieldGreen: '#22C55E',
  warningAmber: '#F59E0B',
  dangerRed: '#DC2626',

  // Intent-named keys — now resolve to Harvest values.
  ink: palette.ink,
  ink2: palette.ink2,
  ink3: palette.ink3,
  stroke: '#EDE2D5',
  stroke2: palette.parchment,
  surface: palette.white,
  surface2: palette.cream,
  surface3: palette.cream2,
  text: palette.ink,
  textDim: palette.inkDim,
  textMute: palette.inkMute,
  brand: palette.signal,
  brandInk: palette.ink,
  brandText: palette.signalText,
  marigold: palette.marigold,
  parchment: palette.parchment,
  good: palette.sage,
  goodSoft: palette.sageSoft,
  goodText: palette.sageText,
  warn: palette.amber,
  warnSoft: palette.amberSoft,
  warnText: palette.amberText,
  danger: palette.alert,
  dangerSoft: palette.alertSoft,
  dangerText: palette.alertText,
  info: palette.info,
  infoSoft: palette.infoSoft,
  infoText: palette.infoText,
  hudBg: palette.graphite,
  hudOverlay: 'rgba(12,11,9,0.55)',
  hudOverlayLight: 'rgba(12,11,9,0.3)',
  hudInk: palette.onDark,
  hudInkDim: palette.onDarkDim,
  hudLine: palette.onDarkLine,
  hudChip: 'rgba(12,11,9,0.55)',

  // Radii & elevation. Radii follow the Harvest scale — 16 on buttons and
  // inputs, 20 on cards, and those two values are the tactile signature.
  r: radius.control,
  r2: radius.item,
  r3: radius.panel,
  r4: radius.surface,
  rPill: radius.pill,
  sh: elevation.sm,
  sh2: elevation.md,
  sh3: elevation.lg,

  // Typography.
  fontUI: typography.ui,
  fontDisplay: typography.display,
  fontMono: typography.metadata,
  font: typography.ui,
  mono: typography.metadata,
  tracking: typography.tracking,

  // Easing & motion.
  easeDefault: motion.ease,
  durationFast: motion.fast,
  durationNormal: motion.slow,

  // Layer 2 semantic CSS-variable handles (see tokens.css).
  semantic: {
    brandPrimary: 'var(--color-brand-primary)',
    surfaceBg: 'var(--color-surface-bg)',
    surfaceCard: 'var(--color-surface-card)',
    surfaceElevated: 'var(--color-surface-elevated)',
    statusSyncing: 'var(--color-status-syncing)',
    statusOffline: 'var(--color-status-offline)',
    borderContrast: 'var(--color-border-contrast)',
    surfaceWorkspace: 'var(--surface-workspace)',
    surfaceRail: 'var(--surface-rail)',
    surfaceEvidence: 'var(--surface-evidence)',
    surfaceHud: 'var(--surface-hud)',
    textPrimary: 'var(--text-primary)',
    textMetadata: 'var(--text-metadata)',
    statusSynced: 'var(--status-synced)',
    actionPrimary: 'var(--action-primary)',
    recordActive: 'var(--record-active)',
    focusRing: 'var(--focus-ring)',
  },
} as const;

export type Tokens = typeof T;
